/**
 * The collections tray, backed by localStorage so a teammate's gathered quotes
 * survive a reload. Storage access is wrapped: a private window or blocked
 * site data must not crash the app, only lose persistence.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import type { CollectedQuote } from "../lib/types";

const STORAGE_KEY = "vy-kb-collections";

interface CollectionsValue {
  readonly quotes: readonly CollectedQuote[];
  add(quote: CollectedQuote): void;
  remove(videoId: string, t: number): void;
  clear(): void;
  has(videoId: string, t: number): boolean;
}

const CollectionsContext = createContext<CollectionsValue | null>(null);

function samePlace(quote: CollectedQuote, videoId: string, t: number): boolean {
  return quote.videoId === videoId && quote.t === t;
}

function loadStored(): CollectedQuote[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function CollectionsProvider({ children }: { children: ReactNode }) {
  const [quotes, setQuotes] = useState<CollectedQuote[]>(loadStored);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(quotes));
    } catch {
      // Storage unavailable (private window / blocked): keep working in memory.
    }
  }, [quotes]);

  const add = useCallback((quote: CollectedQuote) => {
    setQuotes((prev) =>
      prev.some((existing) => samePlace(existing, quote.videoId, quote.t))
        ? prev
        : [...prev, quote],
    );
  }, []);

  const remove = useCallback((videoId: string, t: number) => {
    setQuotes((prev) => prev.filter((quote) => !samePlace(quote, videoId, t)));
  }, []);

  const clear = useCallback(() => {
    setQuotes([]);
  }, []);

  const value = useMemo<CollectionsValue>(
    () => ({
      quotes,
      add,
      remove,
      clear,
      has: (videoId, t) => quotes.some((quote) => samePlace(quote, videoId, t)),
    }),
    [quotes, add, remove, clear],
  );

  return (
    <CollectionsContext.Provider value={value}>
      {children}
    </CollectionsContext.Provider>
  );
}

export function useCollections(): CollectionsValue {
  const value = useContext(CollectionsContext);
  if (value === null) {
    throw new Error("useCollections must be used within a CollectionsProvider");
  }
  return value;
}
